/**
 * Export and import. Two kinds of file, both JSON with a `version` (the file format) and a `schemaVersion` (the shape
 * of the records, see `MIGRATIONS` in storage.ts):
 * - "library": the glossary and the templates, merged by id on import (new entries are added, changed ones are asked
 *   about: keep mine or take the import's);
 * - "backup": everything (glossary, templates, sessions), restored by replacing the data on this device (a phone change).
 *
 * Records of an older schema are migrated on import with the same migrations as the database. When the file shape
 * changes: raise `FORMAT_VERSION`, keep reading the old shape, and add a file of the new version to
 * `tests/fixtures/transfer/` (never edit the existing fixtures).
 */
import { BODY_PARTS, foldText, isVariant, MEASURES, type Exercise } from "./exercises";
import type { Session } from "./sessions";
import { ACTIVE_WORKOUT, COLLECTIONS, MemoryBackend, META, openStorage, SCHEMA_VERSION, type Backend, type Migration, MIGRATIONS } from "./storage";
import { copyName, SET_TYPES, type Template } from "./templates";

export const APP_KEY = "workout-helper";
export const FORMAT_VERSION = 1;
export type FileKind = "library" | "backup";

export interface TransferFile {
  app: typeof APP_KEY;
  kind: FileKind;
  version: number;
  schemaVersion: number;
  /** milliseconds since the epoch */
  exportedAt: number;
  exercises: Exercise[];
  templates: Template[];
  /** backup only */
  sessions?: Session[];
  /** backup only: the seeds this database has had (seed.ts), so a restore does not bring deleted examples back */
  seeds?: string[];
}

type SeedRecord = { id: "seeds"; done: string[] };

/** The glossary and the templates. Hidden exercises are left out unless a template uses them. */
export async function exportLibrary(db: Backend, now: number): Promise<TransferFile> {
  const [exercises, templates] = await Promise.all([db.getAll<Exercise>("exercises"), db.getAll<Template>("templates")]);
  const used = new Set(templates.flatMap((t) => t.exercises.map((x) => x.exerciseId)));
  return {
    app: APP_KEY, kind: "library", version: FORMAT_VERSION, schemaVersion: SCHEMA_VERSION, exportedAt: now,
    exercises: exercises.filter((e) => !e.hidden || used.has(e.id)), templates,
  };
}

/** Everything stored, for a restore on another phone. The workout in progress is not part of it. */
export async function exportBackup(db: Backend, now: number): Promise<TransferFile> {
  const [exercises, templates, sessions, seeds] = await Promise.all([
    db.getAll<Exercise>("exercises"), db.getAll<Template>("templates"), db.getAll<Session>("sessions"), db.get<SeedRecord>(META, "seeds"),
  ]);
  return {
    app: APP_KEY, kind: "backup", version: FORMAT_VERSION, schemaVersion: SCHEMA_VERSION, exportedAt: now,
    exercises, templates, sessions, seeds: seeds?.done ?? [],
  };
}

/** "workout-helper-backup-2026-10-10.json" (local date). */
export function fileName(kind: FileKind, now: number): string {
  const d = new Date(now);
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `${APP_KEY}-${kind === "backup" ? "backup" : "uebungen"}-${day}.json`;
}

// --- reading a file ---

export type ReadError = "notJson" | "notOurs" | "newerVersion" | "damaged";
export type ReadResult = { ok: true; file: TransferFile } | { ok: false; error: ReadError };

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === "string";
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const optNum = (v: unknown) => v === undefined || (isNum(v) && v >= 0);
const isId = (v: unknown) => isStr(v) && v.length > 0;

function validSet(s: unknown, session: boolean): boolean {
  return isObj(s) && (SET_TYPES as readonly unknown[]).includes(s.type)
    && optNum(s.weightKg) && optNum(s.reps) && optNum(s.durationSec) && optNum(s.distanceM)
    && (!session || typeof s.done === "boolean");
}

function validExerciseList(v: unknown, session: boolean): boolean {
  return Array.isArray(v) && v.every((x) => isObj(x) && isId(x.exerciseId) && isNum(x.restSec)
    && (x.measure === undefined || isVariant(x.measure))
    && Array.isArray(x.sets) && x.sets.every((s) => validSet(s, session)));
}

/** Checks the records of the current schema (after migration), so nothing the app cannot show gets stored. */
function validExercise(e: unknown): boolean {
  return isObj(e) && isId(e.id) && isStr(e.name) && e.name.trim() !== "" && isStr(e.equipment)
    && (BODY_PARTS as readonly unknown[]).includes(e.bodyPart) && (MEASURES as readonly unknown[]).includes(e.measure)
    && isNum(e.restSec) && isStr(e.notes) && (e.hidden === undefined || typeof e.hidden === "boolean");
}
const validTemplate = (t: unknown) => isObj(t) && isId(t.id) && isStr(t.name) && isStr(t.notes) && validExerciseList(t.exercises, false);
const validSession = (s: unknown) => isObj(s) && isId(s.id) && isStr(s.name) && isNum(s.startedAt) && isNum(s.endedAt)
  && isStr(s.notes) && (s.templateId === undefined || isStr(s.templateId)) && validExerciseList(s.exercises, true);

const uniqueIds = (list: { id: string }[]) => new Set(list.map((r) => r.id)).size === list.length;

/**
 * Parses and checks a file and brings its records to the current schema. Accepts both kinds; a file from a newer
 * app (format or schema) is refused rather than misread.
 */
export async function readTransferFile(text: string, migrations: Migration[] = MIGRATIONS): Promise<ReadResult> {
  let raw: unknown;
  try {
    raw = JSON.parse(text.replace(/^﻿/, ""));
  } catch {
    return { ok: false, error: "notJson" };
  }
  if (!isObj(raw) || raw.app !== APP_KEY || (raw.kind !== "library" && raw.kind !== "backup")) return { ok: false, error: "notOurs" };
  const current = migrations.reduce((v, m) => Math.max(v, m.to), 1);
  if (!isNum(raw.version) || !isNum(raw.schemaVersion)) return { ok: false, error: "damaged" };
  if (raw.version > FORMAT_VERSION || raw.schemaVersion > current) return { ok: false, error: "newerVersion" };
  const backup = raw.kind === "backup";
  const lists = ["exercises", "templates", ...(backup ? ["sessions"] : [])];
  const records = (v: unknown) => Array.isArray(v) && v.every((r) => isObj(r) && isId(r.id)) && uniqueIds(v as { id: string }[]);
  if (!lists.every((k) => records(raw[k]))) {
    return { ok: false, error: "damaged" };
  }

  // the records go through the database's migrations in a scratch backend
  const scratch = new MemoryBackend();
  await scratch.put(META, { id: "schema", version: raw.schemaVersion } as { id: string });
  for (const k of lists) for (const r of raw[k] as { id: string }[]) await scratch.put(k, r);
  try {
    await openStorage(scratch, migrations);
  } catch {
    return { ok: false, error: "damaged" };
  }
  const file: TransferFile = {
    app: APP_KEY, kind: raw.kind, version: FORMAT_VERSION, schemaVersion: current,
    exportedAt: isNum(raw.exportedAt) ? raw.exportedAt : 0,
    exercises: await scratch.getAll<Exercise>("exercises"),
    templates: await scratch.getAll<Template>("templates"),
    ...(backup ? {
      sessions: await scratch.getAll<Session>("sessions"),
      seeds: Array.isArray(raw.seeds) ? raw.seeds.filter(isStr) : [],
    } : {}),
  };
  const valid = file.exercises.every(validExercise) && file.templates.every(validTemplate) && (file.sessions ?? []).every(validSession);
  return valid ? { ok: true, file } : { ok: false, error: "damaged" };
}

// --- merging glossary and templates ---

/** The same record on this device and in the file, with different content. */
export interface Conflict<T> {
  mine: T;
  theirs: T;
}

export interface ImportPlan {
  /**
   * `matched`: exercises of the file with an id unknown here but the name (and measure) of a visible exercise here,
   * such as the seed exercises, which have random ids on every device. They are not added; the file's templates use
   * this device's exercise instead.
   */
  exercises: { add: Exercise[]; same: number; conflicts: Conflict<Exercise>[]; matched: Conflict<Exercise>[] };
  templates: { add: Template[]; same: number; conflicts: Conflict<Template>[] };
}

/** JSON with sorted keys and without undefined values: equal content gives equal text. */
function canonical(v: unknown): string {
  return JSON.stringify(v, (_, x) => (isObj(x) ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, x[k]])) : x));
}

/** Whether a hidden exercise is shown is this device's business, not the import's. */
const withoutHidden = ({ hidden: _, ...e }: Exercise) => e;

function split<T extends { id: string }>(mine: T[], theirs: T[], key: (r: T) => unknown) {
  const byId = new Map(mine.map((r) => [r.id, r]));
  const out = { add: [] as T[], same: 0, conflicts: [] as Conflict<T>[] };
  for (const r of theirs) {
    const m = byId.get(r.id);
    if (!m) out.add.push(r);
    else if (canonical(key(m)) === canonical(key(r))) out.same++;
    else out.conflicts.push({ mine: m, theirs: r });
  }
  return out;
}

/** What importing the file would do. Exercises the file's templates use come with the file (`exportLibrary`). */
export function planImport(local: { exercises: Exercise[]; templates: Template[] }, file: Pick<TransferFile, "exercises" | "templates">): ImportPlan {
  const known = new Set(local.exercises.map((e) => e.id));
  const byName = new Map(local.exercises.filter((e) => !e.hidden).map((e) => [foldText(e.name), e]));
  const remap = new Map<string, string>();
  const matched: Conflict<Exercise>[] = [];
  const exercises = file.exercises.filter((e) => {
    const mine = known.has(e.id) || e.hidden ? undefined : byName.get(foldText(e.name));
    if (!mine || mine.measure !== e.measure) return true;
    remap.set(e.id, mine.id);
    matched.push({ mine, theirs: e });
    return false;
  });
  const templates = file.templates.map((t) => ({
    ...t, exercises: t.exercises.map((x) => ({ ...x, exerciseId: remap.get(x.exerciseId) ?? x.exerciseId })),
  }));
  return {
    exercises: { ...split(local.exercises, exercises, withoutHidden), matched },
    templates: split(local.templates, templates, (t) => t),
  };
}

/** Nothing would be added or replaced. */
export const isEmptyPlan = (p: ImportPlan) =>
  p.exercises.add.length + p.templates.add.length + p.exercises.conflicts.length + p.templates.conflicts.length === 0;

/** The fields that differ in a conflict (record keys such as "name", "restSec", "exercises"), for the review. */
export function changedFields(c: Conflict<Exercise> | Conflict<Template>): string[] {
  const mine = c.mine as unknown as Record<string, unknown>;
  const theirs = c.theirs as unknown as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(mine), ...Object.keys(theirs)])].filter((k) => k !== "id" && k !== "hidden");
  return keys.filter((k) => canonical(mine[k]) !== canonical(theirs[k]));
}

export const hasConflicts =(p: ImportPlan) => p.exercises.conflicts.length + p.templates.conflicts.length > 0;

/** "mine" keeps this device's version; the default for a conflict without a choice. */
export type Choice = "mine" | "theirs";
/** Choices by record id ("exercise:<id>" / "template:<id>", see `choiceKey`). */
export type Choices = Record<string, Choice>;
export const choiceKey = (kind: "exercise" | "template", id: string) => `${kind}:${id}`;

export interface ImportReport {
  exercisesAdded: number;
  exercisesReplaced: number;
  templatesAdded: number;
  templatesReplaced: number;
  /** imported exercises whose name was taken by another visible exercise: [old name, new name] */
  renamed: [string, string][];
}

/**
 * Writes the plan with the given choices. Exercise names stay unique among the visible exercises: an imported one
 * whose name another exercise has gets a free "(Import)" name. A replaced exercise keeps this device's hidden flag.
 */
export async function applyImport(db: Backend, plan: ImportPlan, choices: Choices, importWord: string): Promise<ImportReport> {
  const report: ImportReport = { exercisesAdded: 0, exercisesReplaced: 0, templatesAdded: 0, templatesReplaced: 0, renamed: [] };
  const all = new Map((await db.getAll<Exercise>("exercises")).map((e) => [e.id, e]));
  const incoming: Exercise[] = [];
  for (const c of plan.exercises.conflicts) {
    if (choices[choiceKey("exercise", c.mine.id)] !== "theirs") continue;
    const { hidden: _, ...theirs } = c.theirs;
    incoming.push(c.mine.hidden ? { ...theirs, hidden: true } : theirs);
    report.exercisesReplaced++;
  }
  incoming.push(...plan.exercises.add.map((e) => ({ ...e })));
  report.exercisesAdded = plan.exercises.add.length;
  for (const e of incoming) all.set(e.id, e);

  for (const e of incoming) {
    if (e.hidden) continue;
    const others = [...all.values()].filter((x) => !x.hidden && x.id !== e.id).map((x) => x.name);
    if (!others.some((n) => foldText(n) === foldText(e.name))) continue;
    const name = copyName(e.name, others, importWord);
    report.renamed.push([e.name, name]);
    e.name = name;
  }
  for (const e of incoming) await db.put("exercises", e);

  for (const c of plan.templates.conflicts) {
    if (choices[choiceKey("template", c.mine.id)] !== "theirs") continue;
    await db.put("templates", c.theirs);
    report.templatesReplaced++;
  }
  for (const t of plan.templates.add) await db.put("templates", t);
  report.templatesAdded = plan.templates.add.length;
  return report;
}

// --- restoring a backup ---

/**
 * Replaces glossary, templates and sessions with the backup's. The backup's records are written first and the ones
 * it does not have deleted after, so an interrupted restore loses nothing of the backup. The seeds both had stay
 * done. Refused while a workout runs (it could use exercises the backup does not have).
 */
export async function restoreBackup(db: Backend, file: TransferFile): Promise<"restored" | "workoutRunning" | "notBackup"> {
  if (file.kind !== "backup") return "notBackup";
  if (await db.get(META, ACTIVE_WORKOUT)) return "workoutRunning";
  const records: Record<(typeof COLLECTIONS)[number], { id: string }[]> = {
    exercises: file.exercises, templates: file.templates, sessions: file.sessions ?? [],
  };
  for (const c of COLLECTIONS) {
    const keep = new Set(records[c].map((r) => r.id));
    for (const r of records[c]) await db.put(c, r);
    for (const r of await db.getAll(c)) if (!keep.has(r.id)) await db.delete(c, r.id);
  }
  const seeds = new Set([...((await db.get<SeedRecord>(META, "seeds"))?.done ?? []), ...(file.seeds ?? [])]);
  const record: SeedRecord = { id: "seeds", done: [...seeds] };
  await db.put(META, record);
  return "restored";
}
